import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { formatCurrency } from '../../utils/formatters';
import { format } from 'date-fns';
import { useTheme } from '../../context/ThemeContext';

export interface ReceiptItem {
  name: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

interface SaleSuccessReceiptProps {
  open: boolean;
  onClose: () => void;
  items: ReceiptItem[];
  total: number;
}

const springSnappy = { type: 'spring' as const, stiffness: 400, damping: 30 };
const springBouncy = { type: 'spring' as const, stiffness: 300, damping: 22 };
const springSmooth = { type: 'spring' as const, stiffness: 200, damping: 25 };
// Fişin aşağı inmesi - gerçek yazıcı hissi için yavaş
const receiptUnroll = { duration: 0.75, ease: [0.22, 0.61, 0.36, 1] as const };

export const SaleSuccessReceipt: React.FC<SaleSuccessReceiptProps> = ({
  open,
  onClose,
  items,
  total,
}) => {
  const receiptDate = format(new Date(), 'dd.MM.yyyy HH:mm');
  const { theme } = useTheme();
  const isLight = theme === 'light';

  const itemsDelay = 0.65;
  const totalDelay = itemsDelay + items.length * 0.06 + 0.12;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="w-full flex flex-col items-center"
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4, transition: { duration: 0.2 } }}
          transition={springSmooth}
        >
          {/* Sepetin altından çıkan POS fiş - üst köşeler card ile aynı (rounded-lg) */}
          <div
            className={`w-full max-w-full overflow-hidden ${
              isLight ? 'border border-slate-400' : 'border border-slate-600'
            }`}
            style={{
              boxShadow: isLight
                ? '0 4px 20px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0,0,0,0.1)'
                : '0 10px 40px -12px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(0,0,0,0.05)',
              borderRadius: '8px 8px 0 0',
            }}
          >
            <motion.div
              className={`origin-top rounded-t-lg ${isLight ? 'bg-slate-900' : 'bg-white'}`}
              style={{ borderRadius: '8px 8px 0 0' }}
              initial={{ scaleY: 0, opacity: 0.9 }}
              animate={{ scaleY: 1, opacity: 1 }}
              transition={{
                ...receiptUnroll,
                delay: 0.05,
              }}
            >
              <div className="px-6 py-4">
                {/* Logo */}
                <motion.div
                  className="flex justify-center mb-3"
                  initial={{ scale: 0.7, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ ...springBouncy, delay: 0.18 }}
                >
                  <img
                    src="/android-chrome-512x512.png"
                    alt="StokFlow"
                    className="w-12 h-12 rounded-lg object-contain"
                  />
                </motion.div>

                {/* Title */}
                <motion.div
                  className="text-center mb-1"
                  initial={{ y: 8, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ ...springSnappy, delay: 0.32 }}
                >
                  <h2 className={`text-lg font-semibold tracking-tight ${isLight ? 'text-white' : 'text-slate-900'}`}>
                    Satış Başarılı
                  </h2>
                </motion.div>

                {/* Date */}
                <motion.div
                  className="text-center mb-4"
                  initial={{ y: 6, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ ...springSnappy, delay: 0.42 }}
                >
                  <span className={`text-xs ${isLight ? 'text-slate-400' : 'text-slate-500'}`}>{receiptDate}</span>
                </motion.div>

                {/* Divider */}
                <motion.div
                  className={`border-t border-dashed mb-4 origin-left ${isLight ? 'border-slate-600' : 'border-slate-200'}`}
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 0.35, delay: 0.52, ease: [0.4, 0, 0.2, 1] }}
                />

                {/* Products */}
                <div className="space-y-2 mb-4">
                  {items.map((item, index) => (
                    <motion.div
                      key={index}
                      className="flex justify-between text-sm"
                      initial={{ opacity: 0, y: 10, x: -4 }}
                      animate={{ opacity: 1, y: 0, x: 0 }}
                      transition={{
                        ...springSnappy,
                        delay: itemsDelay + index * 0.06,
                      }}
                    >
                      <span className={`truncate ${isLight ? 'text-slate-300' : 'text-slate-700'}`}>
                        {item.name} × {item.quantity}
                      </span>
                      <span className={`font-medium ml-2 flex-shrink-0 ${isLight ? 'text-white' : 'text-slate-900'}`}>
                        {formatCurrency(item.total)}
                      </span>
                    </motion.div>
                  ))}
                </div>

                {/* Total */}
                <motion.div
                  className={`border-t pt-3 flex justify-between items-center ${isLight ? 'border-slate-600' : 'border-slate-200'}`}
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ ...springBouncy, delay: totalDelay }}
                >
                  <span className={`font-semibold ${isLight ? 'text-white' : 'text-slate-900'}`}>Toplam</span>
                  <motion.span
                    className={`text-lg font-bold ${isLight ? 'text-white' : 'text-slate-900'}`}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ ...springBouncy, delay: totalDelay + 0.05 }}
                  >
                    {formatCurrency(total)}
                  </motion.span>
                </motion.div>

                {/* Teşekkürler */}
                <motion.div
                  className="text-center mt-4 pt-4"
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ ...springSnappy, delay: totalDelay + 0.12 }}
                >
                  <p className={`text-sm font-medium ${isLight ? 'text-slate-300' : 'text-slate-600'}`}>Teşekkürler</p>
                  <p className={`text-xs mt-1 ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>Bizi tercih ettiğiniz için</p>
                </motion.div>
              </div>
            </motion.div>

            {/* Zigzag - fişin altında tırtıklı kesim (inline SVG) */}
            <motion.svg
              className="block w-full"
              viewBox="0 0 100 10"
              preserveAspectRatio="none"
              style={{ height: 12 }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.55, duration: 0.25 }}
            >
              <path
                fill={isLight ? '#0f172a' : '#ffffff'}
                stroke={isLight ? '#475569' : 'transparent'}
                strokeWidth={isLight ? 0.5 : 0}
                d="M0,0 L100,0 L100,10 L95,5 L90,10 L85,5 L80,10 L75,5 L70,10 L65,5 L60,10 L55,5 L50,10 L45,5 L40,10 L35,5 L30,10 L25,5 L20,10 L15,5 L10,10 L5,5 L0,10 Z"
              />
            </motion.svg>
          </div>

          {/* Kapat butonu */}
          <motion.button
            type="button"
            onClick={onClose}
            className="mt-3 text-xs text-foreground-tertiary hover:text-foreground transition-colors"
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springSnappy, delay: totalDelay + 0.25 }}
          >
            Fişi kapat
          </motion.button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
